/**
 * <cba-cart-items> — the cart, in the drawer and on the cart page (one
 * markup: snippets/cba-cart-lines, -summary, -coupon, -checkout; DESIGN-PLAN
 * 7.G2, "one cart").
 *
 *   <cba-cart-items data-section-id="…" data-gift-code="…" data-coupon-*="…">
 *     <p role="status" data-cba-cart-status data-template="… %N% …"></p>
 *     … [data-cba-cart-part="tier|body|ymal|footer"] …
 *   </cba-cart-items>
 *
 * - Every change goes through @theme/cart, so it dispatches Shopify's
 *   standard cart events. After a change the parts ([data-cba-cart-part])
 *   are replaced from the section the answer carries; a change made
 *   elsewhere (an add, an app) re-renders them the same way.
 * - Quantity: each line is a form posting to /cart/change (it works
 *   without JavaScript). With JavaScript the number on screen changes at
 *   once, the final quantity is sent when the clicks stop (300 ms, as Dawn
 *   and Horizon), and a removed line leaves the list at once. Changes are
 *   sent one at a time, in the order they were made, by line index, and the
 *   cart re-renders after the last: no click is lost while another change is
 *   on its way (site audit B11.1, the lost remove). Amounts that are being
 *   recalculated pulse (brand/identity/05, loading content).
 * - A change the cart declines shows Shopify's reason (role="alert"); a
 *   failed one says so. Focus stays on the control that was used, or moves
 *   to the next line when its line went.
 * - Discount codes: the whole applied set is re-sent on every change, so the
 *   gift code is never dropped by accident. Removing the gift code also
 *   removes the gift lines, in the same request.
 * - The coupon field is the visitor's: whether it is open, what was typed
 *   and its message survive a re-render.
 */
import { changeLine, updateCart, discountCodeProblem, onCartUpdate, loadSection, CartDeclinedError } from '@theme/cart';

if (!customElements.get('cba-cart-items')) {
  const SETTLE_MS = 300;
  /* Shopify's reason a code does not apply (discountCodeProblem) → the
     message that says it (data-coupon-* on the element). Every other reason,
     and none, is "this cart can't use it" ('couponNotApplicable', or
     'couponNotCombinable' when the cart carries a discount). */
  const COUPON_PROBLEMS = {
    DISCOUNT_NOT_FOUND: 'couponInvalid',
    DISCOUNT_CURRENTLY_INACTIVE: 'couponInactive',
    DISCOUNT_USAGE_LIMIT_REACHED: 'couponUsedUp',
    DISCOUNT_CUSTOMER_USAGE_LIMIT_REACHED: 'couponUsedUp'
  };
  const fill = (template, values) => template.replace(/\{\{\s*(\w+)\s*\}\}/g, (m, k) => (k in values ? values[k] : m));

  class CbaCartItems extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.queue = Promise.resolve();
      this.queued = 0;
      this.pending = null; /* { line, timer } — a quantity waiting for the clicks to stop */
      this.lastAnswer = null;
      this.abort = new AbortController();
      const { signal } = this.abort;

      this.addEventListener('submit', (e) => this.onSubmit(e), { signal });
      this.addEventListener('click', (e) => this.onClick(e), { signal });

      /* A change made elsewhere (an add, a card in the drawer, an app's
         updateCart): render from its sections, or fetch ours when it brought
         none. Our own changes render when their queue is done; one that
         lands meanwhile makes that render fetch the section afresh. */
      onCartUpdate((result, event) => {
        if (event.target === this) return;
        if (this.busy()) { this.stale = true; return; }
        const html = result.detail && result.detail.sections && result.detail.sections[this.sectionId];
        if (html) this.render(html);
        else this.refresh();
      }, { signal });
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      if (this.pending) clearTimeout(this.pending.timer);
    }

    t(key, values) { return fill(this.dataset[key] || '', values || {}); }

    busy() { return this.queued > 0 || !!this.pending; }

    /* ── Rendering ── */
    refresh() {
      return loadSection(this.sectionId).then((html) => { if (html) this.render(html); }).catch(() => {});
    }

    render(html) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const fresh = doc.querySelector(`cba-cart-items[data-section-id="${CSS.escape(this.sectionId)}"]`);
      if (!fresh) return;
      const focus = this.focusMark();
      const coupon = this.keepCoupon();
      this.querySelectorAll('[data-cba-cart-part]').forEach((part) => {
        const next = fresh.querySelector(`[data-cba-cart-part="${part.dataset.cbaCartPart}"]`);
        if (!next) return;
        /* A part that names what it shows ([data-cba-cart-key]: the drawer's
           "You may also like") is left alone while the fresh one shows the
           same, so its row keeps what it loaded and where it was scrolled. */
        if (part.dataset.cbaCartKey && part.dataset.cbaCartKey === next.dataset.cbaCartKey) return;
        part.replaceWith(next);
      });
      this.restoreCoupon(coupon);
      this.restoreFocus(focus);
      this.dispatchEvent(new CustomEvent('cba-cart-items:render', { bubbles: true }));
    }

    /* Which control had focus, by line index and role, so the same control
       (or its line's next one) gets it back after the parts are replaced. */
    focusMark() {
      const active = document.activeElement;
      if (!active || !this.contains(active)) return null;
      const line = active.closest('[data-cba-line]');
      return {
        id: active.id,
        line: line ? Number(line.dataset.cbaLine) : 0,
        action: active.dataset.cbaAction || ''
      };
    }

    restoreFocus(mark) {
      if (!mark) return;
      if (mark.id && !mark.line) {
        const el = document.getElementById(mark.id);
        if (el && this.contains(el)) { el.focus({ preventScroll: true }); return; }
      }
      this.focusLine(mark.line, mark.action);
    }

    /* The control `action` on line `n`, else that line's first control, else
       the line before it, else the cart's heading. */
    focusLine(n, action) {
      const lines = this.querySelectorAll('[data-cba-line]');
      const line = this.querySelector(`[data-cba-line="${n}"]`) || lines[lines.length - 1];
      const target = line && (line.querySelector(`[data-cba-action="${action}"]`) || line.querySelector('button, a[href]'));
      if (target) { target.focus({ preventScroll: true }); return; }
      const heading = this.querySelector('[data-cba-cart-heading]');
      if (heading) heading.focus({ preventScroll: true });
    }

    keepCoupon() {
      const node = this.querySelector('.cba-cart-coupon__body');
      if (!node) return null;
      const active = document.activeElement;
      return {
        node,
        open: !!this.querySelector('[data-cba-coupon].is-open'),
        focusId: node.contains(active) && active.id ? active.id : ''
      };
    }

    restoreCoupon(kept) {
      if (!kept) return;
      const fresh = this.querySelector('.cba-cart-coupon__body');
      const wrap = this.querySelector('[data-cba-coupon]');
      const trigger = this.querySelector('[data-cba-coupon-trigger]');
      if (!fresh || !wrap) return;
      fresh.replaceWith(kept.node);
      wrap.classList.toggle('is-open', kept.open);
      if (trigger) trigger.setAttribute('aria-expanded', String(kept.open));
      const focus = kept.focusId && document.getElementById(kept.focusId);
      if (focus) focus.focus({ preventScroll: true });
    }

    /* ── One change at a time ── `task` runs after every change before it.
       When the last is done the parts re-render from its answer, or from a
       fresh fetch when it failed or another change landed meanwhile. */
    enqueue(task) {
      this.queued++;
      this.setBusy(true);
      this.queue = this.queue
        .then(task)
        .then(
          (answer) => { this.lastAnswer = answer || null; },
          (error) => { this.lastAnswer = null; if (!(error && error.handled)) this.showError(error); }
        )
        .then(() => {
          this.queued--;
          if (!this.busy()) this.settle();
        });
      return this.queue;
    }

    settle() {
      const answer = this.stale ? null : this.lastAnswer;
      this.stale = false;
      this.lastAnswer = null;
      this.setBusy(false);
      const html = answer && answer.sections && answer.sections[this.sectionId];
      const rendered = html ? Promise.resolve(this.render(html)) : this.refresh();
      rendered.then(() => this.announce(answer && answer.cart ? answer.cart.item_count : null));
    }

    setBusy(on) {
      this.toggleAttribute('data-cba-busy', on);
      this.querySelectorAll('[data-cba-cart-part="body"], [data-cba-cart-part="footer"]').forEach((part) => part.setAttribute('aria-busy', String(on)));
    }

    /* The message sits outside the parts, so a re-render keeps it. */
    showError(error) {
      const el = this.querySelector('[data-cba-cart-error]');
      if (!el) return;
      el.textContent = error instanceof CartDeclinedError && error.message ? error.message : this.t('error');
      el.hidden = false;
    }

    clearError() {
      const el = this.querySelector('[data-cba-cart-error]');
      if (el) { el.hidden = true; el.textContent = ''; }
    }

    /* Screen-reader status after a change (WCAG 4.1.3). */
    announce(count) {
      const el = this.querySelector('[data-cba-cart-status]');
      if (!el || typeof count !== 'number') return;
      el.textContent = '';
      setTimeout(() => { el.textContent = el.getAttribute('data-template').replace('%N%', count); }, 50);
    }

    /* ── Quantity ── */
    onSubmit(e) {
      if (e.target.closest('[data-cba-coupon-form]')) { e.preventDefault(); this.applyCoupon(); return; }
      const form = e.target.closest('[data-cba-line-form]');
      if (!form) return;
      e.preventDefault();
      const button = e.submitter;
      const line = form.closest('[data-cba-line]');
      if (!button || !line) return;
      this.onQuantity(line, button.dataset.cbaAction);
    }

    onQuantity(line, action) {
      const n = Number(line.dataset.cbaLine);
      const numEl = line.querySelector('[data-cba-line-qty]');
      const current = numEl ? parseInt(numEl.textContent, 10) || 0 : 1;
      const quantity = action === 'increase' ? current + 1 : action === 'decrease' ? Math.max(0, current - 1) : 0;
      this.clearError();

      /* Another line waiting: it goes first, as it was clicked first. */
      if (this.pending && this.pending.line !== n) this.flush();

      if (quantity === 0) {
        if (this.pending) { clearTimeout(this.pending.timer); this.pending = null; }
        this.removeLine(line);
        this.send(n, 0, line.dataset.key);
        return;
      }
      if (numEl) numEl.textContent = quantity;
      if (this.pending) clearTimeout(this.pending.timer);
      this.pending = { line: n, key: line.dataset.key, timer: setTimeout(() => this.flush(), SETTLE_MS) };
      this.setBusy(true);
    }

    flush() {
      const p = this.pending;
      if (!p) return;
      clearTimeout(p.timer);
      this.pending = null;
      const line = this.querySelector(`[data-cba-line="${p.line}"]`);
      const numEl = line && line.querySelector('[data-cba-line-qty]');
      this.send(p.line, numEl ? parseInt(numEl.textContent, 10) || 0 : 0, p.key);
    }

    send(n, quantity, key) {
      this.enqueue(() => changeLine(n, quantity, { element: this, key }));
    }

    /* A removed line leaves at once, and the lines after it move up one
       place, so a click on them names the right line before the cart has
       answered. Focus goes to the line that took its place. */
    removeLine(line) {
      const n = Number(line.dataset.cbaLine);
      const hadFocus = line.contains(document.activeElement);
      line.remove();
      this.querySelectorAll('[data-cba-line]').forEach((other) => {
        const m = Number(other.dataset.cbaLine);
        if (m > n) other.dataset.cbaLine = String(m - 1);
      });
      if (hadFocus) this.focusLine(n, 'remove');
    }

    /* ── Discount codes (Shopify /cart/update.js {discount}) ──
       EVERY code on the cart (server-rendered into data-applied-codes),
       including the gift code, which has no removable chip. */
    appliedCodes() {
      const wrap = this.querySelector('[data-cba-coupon]');
      return wrap ? (wrap.getAttribute('data-applied-codes') || '').split('|').filter(Boolean) : [];
    }

    applyCoupon() {
      const input = this.querySelector('[data-cba-coupon-input]');
      const code = input ? input.value.trim().toUpperCase() : '';
      if (!code) { this.showMsg(this.t('couponMissing'), 'error'); return; }
      const existing = this.appliedCodes();
      if (existing.some((c) => c.toUpperCase() === code)) {
        this.showMsg(this.t('couponAlreadyApplied', { code }), 'success');
        return;
      }
      this.setApplying(true);
      this.enqueue(() => updateCart({ discountCodes: existing.concat([code]) }, { element: this })
        .then((answer) => {
          if (codeIsApplied(answer.cart, code)) {
            if (input) input.value = '';
            this.showMsg(this.t('couponApplied', { code }), 'success');
            return answer;
          }
          /* Not applicable: ask Shopify why while the cart still holds the
             code, go back to the codes before (the cart is unchanged) and say
             it. The reason was guessed from the cart until 2026-10-10: any
             discount on it turned "no such code" into "can't be combined"
             (site audit B11.5; Leo found it with the gift in the cart). */
          return discountCodeProblem(answer.cart, existing.concat([code]), code)
            .then((problem) => updateCart({ discountCodes: existing }, { element: this }).then((back) => {
              /* Shopify gives one reason (DISCOUNT_CODE_NOT_HONOURED) for a
                 code the cart does not qualify for and for one that does not
                 combine with a discount on it (verified 2026-10-10), so a
                 cart with a discount gets the second as a possibility. */
              const refused = (back.cart.total_discount || 0) > 0 ? 'couponNotCombinable' : 'couponNotApplicable';
              this.showMsg(this.t(COUPON_PROBLEMS[problem] || refused, { code }), 'error');
              return back;
            }));
        })
        .catch((error) => {
          this.showMsg(this.t('couponError'), 'error');
          error.handled = true;
          throw error;
        })
        .finally(() => this.setApplying(false)));
    }

    /* Removing the gift code also removes the free gift lines (or the
       customer would keep the gift at full price), in the same request, and
       clears the gift logic's session flags. */
    removeCoupon(code, button) {
      const giftCode = String(this.dataset.giftCode || '').toLowerCase();
      const updates = {};
      if (giftCode && code.toLowerCase() === giftCode) {
        try {
          sessionStorage.removeItem('gift_added_' + giftCode);
          sessionStorage.removeItem('discount_set_' + giftCode);
        } catch (e) { /* storage blocked: nothing to clear */ }
        this.querySelectorAll('[data-cba-line][data-cba-gift]').forEach((line) => { updates[line.dataset.key] = 0; });
      }
      if (button) button.disabled = true;
      const remaining = this.appliedCodes().filter((c) => c !== code);
      this.enqueue(() => updateCart({ discountCodes: remaining, updates }, { element: this })
        .catch((error) => {
          if (button) button.disabled = false;
          this.showMsg(this.t('couponRemoveError'), 'error');
          error.handled = true;
          throw error;
        }));
    }

    setApplying(on) {
      const apply = this.querySelector('[data-cba-coupon-apply]');
      if (!apply) return;
      apply.disabled = on;
      apply.setAttribute('aria-busy', String(on));
      apply.textContent = on ? this.t('couponApplying') : this.t('couponApply');
    }

    showMsg(text, type) {
      const msg = this.querySelector('[data-cba-coupon-msg]');
      if (!msg) return;
      msg.textContent = text;
      msg.className = 'cba-cart-coupon__msg ' + type + (type === 'error' ? ' cba-field-error' : '');
      /* The field says it is invalid and points at the reason (WCAG 3.3.1). */
      const input = this.querySelector('[data-cba-coupon-input]');
      if (!input) return;
      if (type === 'error') {
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', msg.id);
      } else {
        input.removeAttribute('aria-invalid');
        input.removeAttribute('aria-describedby');
      }
    }

    onClick(e) {
      const trigger = e.target.closest('[data-cba-coupon-trigger]');
      if (trigger) {
        const wrap = this.querySelector('[data-cba-coupon]');
        const open = wrap.classList.toggle('is-open');
        trigger.setAttribute('aria-expanded', String(open));
        const input = this.querySelector('[data-cba-coupon-input]');
        if (open && input) input.focus();
        return;
      }
      const remove = e.target.closest('[data-remove-code]');
      if (remove) this.removeCoupon(remove.getAttribute('data-remove-code'), remove);
    }
  }

  /* True when `code` is on the cart AND applies. Falls back to a matching
     cart-level discount if the answer has no discount_codes. */
  function codeIsApplied(cart, code) {
    const upper = code.toUpperCase();
    const found = (cart.discount_codes || []).find((c) => (c.code || '').toUpperCase() === upper);
    if (found) return found.applicable !== false;
    return (cart.cart_level_discount_applications || []).some((a) => (a.title || '').toUpperCase() === upper);
  }

  customElements.define('cba-cart-items', CbaCartItems);
}
